import React, {FC, useState} from "react";
import {useAutoRetry} from "@hooks";
import {useTokenPairRelevantList} from "@hooks/token.ts";
import {Skeleton} from "@components/ui/skeleton.tsx";
import {Button} from "@components/ui/button.tsx";


export type SelectProps = {
    onSelect: (pair: number) => void;
};

export const Select: FC<SelectProps> = ({onSelect}) => {
    const [query, setQuery] = useState("");
    const [selectedPair, setSelectedPair] = useState<number | null>(null);

    const [pairs, loading, error, reload] = useTokenPairRelevantList();
    const MAX_ATTEMPTS = 5;

    const [retryCount, retrying, secondsRemaining, resetRetry] = useAutoRetry({
        shouldRetry: !pairs && !!error,
        maxAttempts: MAX_ATTEMPTS,
        retryCallback: reload,
    });

    if (!pairs || loading || retrying) {
        return (
            <div className="pt-10 flex flex-col items-center space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                    {Array(6).fill(null).map((_, index) => (
                        <Skeleton key={index} className="h-16 w-full rounded-xl bg-orange-100/20"/>
                    ))}
                </div>
                {retrying && (
                    <p className="text-orange-300 text-md">
                        Failed to load. Trying again ({retryCount + 1}/{MAX_ATTEMPTS})...
                    </p>
                )}
                {retrying && secondsRemaining > 0 && (
                    <p className="text-orange-300 text-sm">
                        Retrying in {secondsRemaining}s
                    </p>
                )}
            </div>
        );
    }

    if (error && retryCount >= 3) {
        return (
            <div className="pt-32 flex flex-col space-y-6 items-center justify-center text-center">
                <p className="text-red-400">Failed to load relevant tokens after {MAX_ATTEMPTS} attempts.</p>
                <button
                    onClick={() => {
                        resetRetry();
                        reload();
                    }}
                    className="bg-orange-400 hover:bg-orange-500 text-white px-4 py-2 rounded-xl transition"
                >
                    Try again
                </button>
            </div>
        );
    }

    const handleSelect = (pair: number) => {
        setSelectedPair(pair);
        onSelect(pair);
    };

    return (
        <div className="w-full max-w-md mx-auto bg-white/5 backdrop-blur-md rounded-2xl p-6 space-y-6 border border-orange-300/10 shadow-lg">
            {/*<input*/}
            {/*    type="text"*/}
            {/*    placeholder="Search tokens or symbols"*/}
            {/*    className="w-full px-4 py-2 rounded-xl border border-orange-300/30 bg-white/10 text-sm text-orange-100 placeholder-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-300 transition"*/}
            {/*    value={query}*/}
            {/*    onChange={(e) => setQuery(e.target.value)}*/}
            {/*/>*/}

            <div className="space-y-6 max-h-80 overflow-y-auto pr-1">
                {pairs.map((pair) => {
                    const base = pair.base.token;

                    return (
                        <div key={base.mint} className="space-y-3">
                            <div className="flex items-center">
                                {base.image ? (
                                    <img
                                        src={base.image}
                                        alt={base.symbol}
                                        className="h-8 w-8 rounded-full mr-3 border border-orange-300/40"
                                    />
                                ) : (
                                    <div
                                        className="h-8 w-8 rounded-full bg-orange-300 text-orange-900 font-bold text-sm flex items-center justify-center mr-3">
                                        {base.symbol?.charAt(0)}
                                    </div>
                                )}
                                <div className="text-sm font-semibold text-orange-200">{base.name}</div>
                            </div>

                            <div className="pl-11 flex flex-wrap gap-2">
                                {pair.quotes.map((q) => {
                                    const symbol = `${base.symbol}/${q.token.symbol}`;
                                    const isSelected = selectedPair === q.pair;

                                    return (
                                        <Button
                                            key={base.mint + q.token.mint}
                                            onClick={() => handleSelect(q.pair)}
                                            variant="ghost"
                                            className={`text-xs font-medium px-3 py-1 rounded-full border ${
                                                isSelected
                                                    ? "bg-orange-400/30 border-orange-400 text-orange-100"
                                                    : "border-orange-300 text-orange-200 hover:bg-orange-300/10"
                                            } transition`}
                                        >
                                            {symbol}
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                {pairs.length === 0 && (
                    <p className="text-sm text-orange-300 text-center py-2">No relevant tokens found</p>
                )}
            </div>
        </div>
    );
};